import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { confirmForgotPassword, setAuthError } from '../store/slices/authSlice';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import MyTextField from '../components/MyTextField';
import MySnackbar from '../components/MySnackbar';
import LoadingComponent from '../components/LoadingComponent';
import s10Logo from '../assets/images/s10Logo.png';
import authImage from '../assets/images/auth.png';

const ResetPassword = () => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, error } = useSelector((state: RootState) => state.auth);
    
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [codeError, setCodeError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmError, setConfirmError] = useState('');

    const email = location.state?.email || '';

    const validateForm = (): boolean => {
        let isValid = true;

        if (!code.trim()) {
            setCodeError('El código es requerido');
            isValid = false;
        } else {
            setCodeError('');
        }

        if (!newPassword) {
            setPasswordError('La nueva contraseña es requerida');
            isValid = false;
        } else if (newPassword.length < 6) {
            setPasswordError('La contraseña debe tener al menos 6 caracteres');
            isValid = false;
        } else {
            setPasswordError('');
        }

        if (newPassword !== confirmPassword) {
            setConfirmError('Las contraseñas no coinciden');
            isValid = false;
        } else {
            setConfirmError('');
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        const result = await dispatch(confirmForgotPassword({
            email,
            code,
            newPassword,
        }));

        if (confirmForgotPassword.fulfilled.match(result)) {
            navigate('/login', { state: { message: 'Contraseña restablecida exitosamente' } });
        }
    };

  return (
    <Grid container style={{ minHeight: "100vh", margin: "0 !important" }}>
        <Grid
            container
            item
            xs={12}
            md={6}
            direction="column"
            justifyContent="center"
            alignItems="center"
            style={{
                backgroundColor: theme.s10palette?.S10Blueblue50,
                minHeight: "100vh",
                padding: "2rem 0",
            }}
        >
            <MySnackbar
                open={!!error}
                autoHideDuration={6000}
                msg={error || ''}
                severity="error"
                onClose={() => dispatch(setAuthError(''))}
            />

            <Grid
                container
                direction="row"
                maxWidth="20rem"
                alignItems="center"
                rowSpacing="1rem"
                component="form"
                onSubmit={handleSubmit}
            >
                <Grid justifyContent="center" alignItems="center" alignContent="center" item xs={12} style={{ marginTop: ".5rem" }}>
                    <img style={{ width: "8.75rem" }} src={s10Logo} alt="S10" />
                </Grid>
                <Typography variant="h3" style={{ marginBottom: "0.5rem" }}>
                    ¡Bienvenido!
                </Typography>
                <Typography variant="h6">Portal Mis Boletas - Restablecer contraseña</Typography>
                <Divider
                    style={{
                        width: "100%",
                        marginTop: "1.19rem",
                        marginBottom: "1.5rem",
                        color: theme.s10palette.S10Blueblue100,
                    }}
                />
                <Typography variant="body1" align="left" width="100%">
                    Ingresa el código que recibiste por email y tu nueva contraseña
                </Typography>

                <MyTextField
                    id="email"
                    label="Email"
                    type="email"
                    value={email}
                    disabled
                    fullWidth
                />

                <MyTextField
                    id="code"
                    label="Código de verificación"
                    type="text"
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                    error={!!codeError}
                    helperText={codeError}
                    fullWidth
                />

                <MyTextField
                    id="newPassword"
                    label="Nueva contraseña"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                    error={!!passwordError}
                    helperText={passwordError}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <MyTextField
                    id="confirmPassword"
                    label="Confirmar contraseña"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    error={!!confirmError}
                    helperText={confirmError}
                    fullWidth
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Grid container width="20rem" spacing={2} style={{ marginTop: "1rem" }}>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => navigate('/forgot-password')}
                        >
                            Volver
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={isLoading}
                        >
                            Confirmar
                        </Button>
                    </Grid>
                </Grid>

                <Typography variant="body2" style={{ marginTop: '10px' }}>
                    <Link to="/login" color="primary">
                        Volver al inicio de sesión
                    </Link>
                </Typography>
            </Grid>
        </Grid>

        <Grid
            item
            md={6}
            sx={{ display: { xs: 'none', md: 'block' } }}
            style={{
                backgroundColor: theme.s10palette?.S10Blueblue700,
                backgroundImage: `url(loginDefaultImage.svg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 0,
                }}
            >
                <img
                    src={authImage}
                    alt="Ilustración auth"
                    style={{
                        maxWidth: '100%',
                        maxHeight: '90%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
            </Box>
        </Grid>

        {isLoading && <LoadingComponent loadingText="Restableciendo contraseña..." />}
    </Grid>
  );
};

export default ResetPassword;